<template>
  <div class="oring-results-grid-wrapper custom-scrollbar">
    <Grid
      :table-columns="tableColumns"
      :data-items="dataItems"
      disable-paging
      class="oring-results-grid"
      :scrollable="'none'"
    >
      <template #sku="{props}">
        <td class="sku-cell">
          <div class="cell-with-icon">
            <div class="sku-content">
              <NuxtLink
                :to="getDetailLinkUrl(props.dataItem)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <NavigationMenu />
                {{ props.dataItem[props.field] }}
              </NuxtLink>
            </div>
            <div class="mobile-item-details d-block d-md-none">
              <NuxtLink
                :to="app.$globalization.localePath('/oring-calculator/oring-assistant/details', i18n.locale.value, app.$globalization.getRegion())"
                data-gtm="oring_itemdetails"
                @click="setOringSkuAndNavigate(props.dataItem.articleNo, props.dataItem.d1, props.dataItem.d2)"
              >
                <ArrowRight />
                <span class="mobile-item-label">
                  {{ getColumnTitle('detailsPage') }}
                </span>
              </NuxtLink>
            </div>
          </div>
        </td>
      </template>
      <template #availability="{props}">
        <td
          :data-th="getColumnTitle(props.field)"
          :class="[props.className]"
        >
          <span :class="props.dataItem[props.field] ? 'availability-value oring-available' : 'availability-value oring-unavailable'">
            {{ props.dataItem[props.field] ? i18n.tv('Frontend.Parameter.OnStock', 'ORingConfiguratorImport') : i18n.tv('Frontend.Parameter.OnRequest', 'ORingConfiguratorImport') }}
          </span>
        </td>
      </template>
      <template #dimensionCell="{props}">
        <td :data-th="getColumnTitle(props.field)">
          <div class="td-content">
            <span>{{ props.dataItem[props.field] }}</span>
            <span class="td-content__unit-spacing">{{ isInchComputed }}</span>
          </div>
        </td>
      </template>
      <template #stretching="{props}">
        <td :data-th="getColumnTitle(props.field)">
          <div class="td-content-stretching">
            <div class="td-content-stretching__values">
              <span
                v-if="(isDesignCockpit || props.field !== 'fillingRatio') && tableColumns.find(col => col.field === props.field)?.minField"
                :class="getColorClass(props.dataItem[props.field + 'MinColorRating'])"
              >
                <span class="value-label">{{ i18n.tv('Frontend.Parameter.MinValue', 'ORingConfiguratorImport') }}</span>
                {{ props.dataItem[props.field + 'Min'] }} %
              </span>
              <span
                v-if="(isDesignCockpit && tableColumns.find(col => col.field === props.field)?.minField) || (!isDesignCockpit && props.field === 'fillingRatio' && tableColumns.find(col => col.field === props.field)?.meanField)"
                :class="getColorClass(props.dataItem[props.field + 'MeanColorRating'])"
              >
                <span class="value-label"> Ø </span>
                {{ props.dataItem[props.field + 'Mean'] }} %
              </span>
              <span :class="getColorClass(props.dataItem[props.field + 'MaxColorRating'])">
                <span class="value-label">max.</span>
                {{ props.dataItem[props.field + 'Max'] }} %
              </span>
            </div>
          </div>
        </td>
      </template>
      <template #detailsPage="{props}">
        <td :data-th="getColumnTitle(props.field)">
          <div class="details-page">
            <NuxtLink
              :to="app.$globalization.localePath('/oring-calculator/oring-assistant/details', i18n.locale.value, app.$globalization.getRegion())"
              data-gtm="oring_itemdetails"
              @click="setOringSkuAndNavigate(props.dataItem.articleNo, props.dataItem.d1, props.dataItem.d2)"
            >
              <ArrowRight />
            </NuxtLink>
          </div>
        </td>
      </template>
    </Grid>
  </div>
</template>
<script setup lang="ts">
import Grid from '~/components/grid/Grid'
import ArrowRight from '~/assets/icons/md_arrow_forward.svg?component'
import NavigationMenu from '~/assets/icons/open_in_new.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import {useORingCalculatorStore} from '@/store/oring-calculator'

const app = useNuxtApp()
const i18n = useI18nExtended()
const oringStore = useORingCalculatorStore()

const props = defineProps({
  tableColumns: {
    type: Array,
    required: true,
    default: () => []
  },
  dataItems: {
    type: Array,
    required: true
  },
  isInchComputed: {
    type: String,
    required: false,
    default: 'mm'
  },
  isDesignCockpit: {
    type: Boolean,
    required: false,
    default: false
  }
})

const setOringSkuAndNavigate = (sku: number, d1: number, d2: number) => {
  oringStore.oringIsAssistantDetails = !props.isDesignCockpit
  oringStore.oringSku = sku
  oringStore.oringDetailsD1 = d1
  oringStore.oringDetailsD2 = d2
}

const getDetailLinkUrl = (product: any): string => {
  // Return a relative, locale-aware path so Nuxt treats it as an internal route
  // and can prefetch for faster navigation in a new tab.
  return app.$globalization.localePath('/products/' + product.articleNo, i18n.locale.value, app.$globalization.getRegion())
}

const getColorClass = (rating: number): string => {
  switch (rating) {
  case 0: return 'rating-red'
  case 1: return 'rating-yellow'
  case 2: return 'rating-green'
  default: return ''
  }
}

const getColumnTitle = (fieldName: string) => {
  if (fieldName === 'detailsPage') {
    const column = props.tableColumns.find((c: any) => c.cell === 'detailsPage')
    return (column as any)?.title || ''
  }
  const column = props.tableColumns.find((c: any) => c.field === fieldName)
  return (column as any)?.title || ''
}
</script>
<style lang="scss">

.oring-results-grid-wrapper {
  max-height: 20.5rem;
  overflow: auto;

  .mobile-item-details {
    display: none;
  }

  .oring-results-grid {
    .k-column-title {
      color: $color__fst__primary;
      font-weight: $base-text-font-weight-light;
    }

    .k-table-tbody {
      font-weight: $base-text-font-weight-light;
    }

    .k-grid-header {
      background-color: $color__fst__white;
      border: none;
      border-right: 0;
      border-left: 0;
      border-top: 0;
    }

    .cell-with-icon {
      display: flex;
      gap: .3rem;
      align-items: center;

      svg {
        color: $color__primary;
      }
    }
  }

  .k-grid .k-grid-header {
    background-color: $color__white;
    border: none;
    color: $color__fst__primary;

    tr {
      th {
        position: sticky;
        background-color: $color__white;
      }
    }
  }

   .k-grid-header .k-header.right-align-header {
      justify-content: flex-end;
    }

  .k-grid {
    border: none;

    .k-grid-aria-root {
      overflow: visible;
    }

    td {
      border-width: 0;
      border-bottom: 0.1875rem solid $color__fst__gray__020;
      vertical-align: middle;
    }

    .k-table-th {
      border-width: 0;
      border-bottom: 0.0625rem solid $color__fst__primary;
    }

    .k-table-row.k-table-alt-row {
      background-color: $color__fst__white;
    }

    a {
      color: $color__fst__cyan__100;
      font-weight: $base-text-font-weight-semi-bold;
    }

    .details-page {
      .icon.icon--svg {
        color: $color__fst__primary;
        height: 1.5rem;
        width: 1.5rem;
      }
    }

  }

  .oring-available {
    color: $color__fst__success__contrast;
    font-weight: $base-text-font-weight-semi-bold;
  }

  .oring-unavailable {
    color: $color__fst__warning__contrast;
    font-weight: $base-text-font-weight-semi-bold;

  }

  .td-content {
    display: flex;
    justify-content: flex-end;

    &__unit-spacing {
      padding-left: .2rem;
    }
  }

  .td-content-stretching {
    display: flex;
    justify-content: flex-end;

    &__values {
      display: flex;
      flex-direction: column;

      .value-label {
        font-weight: $base-text-font-weight-light;
      }
    }
  }

  .rating-red,
  .rating-yellow,
  .rating-green {
    display: flex;
    justify-content: flex-end;

    .value-label{
      padding-right: .2rem;
    }
  }

  .rating-red {
    color: $color__fst__error__contrast;
  }

  .rating-yellow {
    color: $color__fst__warning__contrast;
  }

  .rating-green {
    color: $color__fst__success__contrast;
  }

  .k-grid .k-table-tbody>.k-table-row:not(.k-detail-row):hover {
    background-color: transparent;
  }
}

@include breakpoint-down($sm) {
  .oring-results-grid-wrapper {
    max-height: none;
    overflow-x: hidden;
    width: calc(100vw - 1rem);
    padding: 0;
    margin: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);

    .mobile-item-details {
      display: flex;
    }

    .k-grid {
      width: 100%;

      table {
        thead { display: none; }

        tr {
          display: flex;
          flex-direction: column;
          padding: 1rem;
          margin: 0.2rem 0 1rem 1.2rem;
          box-shadow: $shadow-default;
          border-radius: 0.25rem;
          background: $color__white;
          width: calc(100vw - 2.5rem);
          box-sizing: border-box;
        }

        td {
          font-weight: $base-text-font-weight-semi-bold;
          display: flex;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 0.0625rem solid $color__fst__gray__030;
          position: relative;
          width: 100%;
          box-sizing: border-box;
          gap: 1rem;

          &:last-child {
            border-bottom: none;
          }

          &:nth-last-child(2) {
            border-bottom: none ;
            padding-bottom: 0;
          }

          &:has(.details-page) {
            display: none;
          }

          &[data-th]::before {
            content: attr(data-th);
            margin-right: 0.5rem;
            font-weight: $base-text-font-weight-light;
            text-transform: none;
            flex: 0 0 40%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          &.sku-cell {
            border-bottom: none;

            &::before { display: none; }

            > .cell-with-icon {
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
              overflow: hidden;
            }

            .sku-content {
              flex: 1 1 auto;
              overflow: hidden;
              white-space: nowrap;
              width: 50%;
            }

            .mobile-item-details {
              display: flex;
              align-items: center;
              flex: 0 0 auto;
              width: 50%;

              a {
                display: flex;
                align-items: center;
                line-height: 1;
              }

              .icon.icon--svg, svg {
                width: 1.25rem;
                height: 1.25rem;
                flex-shrink: 0;
              }
            }

            .mobile-item-label {
              display: inline-block;
              flex: 0 0 auto;
              font-weight: $base-text-font-weight-semi-bold;
              margin-left: .2rem;
              line-height: 1.25rem; /* match icon height for better centering */
              vertical-align: middle;
            }
          }

          .cell-with-icon,
          .td-content,
          .td-content-stretching {
            justify-content: flex-start;
            flex: 1;
            text-align: left;
            min-width: 0;
            overflow: hidden;

            .value-label {
              font-weight: $base-text-font-weight-semi-bold;
            }
          }

          .cell-with-icon {
            justify-content: space-between;
            align-items: center;

            .sku-content {
              display: flex;
              align-items: center;
              gap: .3rem;
            }

            .mobile-item-details {
              display: flex;
              align-items: center;
              color: $color__fst__cyan__100;

              .icon.icon--svg {
                height: 1.5rem;
                width: 1.5rem;
              }
            }
          }

          .oring-available,
          .oring-unavailable {
            font-weight: $base-text-font-weight-semi-bold;
          }

          &[data-th="Availability"].oring-available {
            color: inherit;
          }

          &[data-th="Availability"].oring-unavailable {
            color: inherit;
          }

          .td-content-stretching__values {
            align-items: flex-start;
            text-align: left;
          }

          .mobile-item-details[data-th] {
            display: flex;
            align-items: center;
          }

          .mobile-item-details[data-th]::before {
            content: attr(data-th);
            font-weight: $base-text-font-weight-light;
            margin-right: 0.5rem;
            white-space: nowrap;
          }
        }
      }
    }
  }

  .k-grid table td.sku-cell {
    overflow: hidden;

    > .cell-with-icon {
      display: flex;
      align-items: center;
      width: 100%;
      flex-wrap: nowrap;
      overflow: hidden;
    }

    .sku-content {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
    }

    .mobile-item-details {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      white-space: nowrap;
      justify-content: flex-start;
      margin-right: 1rem;
    }

    .mobile-item-label {
      margin-right: 0.25rem;
      flex: 0 0 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
